
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action, computed } from "mobx";

// MARK: Components
import { IRowItem, ColumnType } from "../../components/Table/TableRow";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore, routerStore, languageStore } from "../_rootStore";
import ImageService from "../../services/ImageService";

// MARK: Resources
import strings from "../../resources/strings";

export default class RoomStore extends VariableChangeHandler {
	// Services
	public imageService: ImageService = new ImageService();
	public layoutsImageService: ImageService = new ImageService();

	// Control
	@observable public loading: boolean = false;
	@observable public onEditMode: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public rooms: api.Room[] = [];
	@observable public selectedRoom: api.Room | null = null;

	// Variables
	@observable public order: string = "0";
	@observable public name: string = "";
	@observable public nick: string = "";
	@observable public description: string = "";
	@observable public url: string = "";

	@computed
	public get header() {
		const header = strings.rooms.table.header;
		return [
			header.picture,
			header.nick,
			header.name,
		];
	}

	private static formatRoom(room: api.Room): IRowItem {
		const roomInSelectedLanguage = room.languages.filter((roomLanguage) =>
		roomLanguage.language === languageStore.selectedLanguage)[0].translation;

		return {
			id: room.id,
			name: roomInSelectedLanguage.name,
			columns: [
				{
					value: roomInSelectedLanguage.layouts[0].url,
					type: ColumnType.avatar,
				},
				{
					value: roomInSelectedLanguage.nick,
				},
				{
					value: roomInSelectedLanguage.name,
				},
			],
		};
	}

	@computed
	public get roomsTableRows(): IRowItem[] {
		return this.rooms.map(RoomStore.formatRoom);
	}

	@action
	public getRoom = async (roomId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			this.selectedRoom = await api.getRoom(roomId);
			this.setEditorFields(this.selectedRoom);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public getRooms = async (pageOffSet?: number) => {
		if (this.loading) {
			return;
		}

		if (!pageOffSet) {
			pageOffSet = 0;
		}

		this.loading = true;

		try {
			this.rooms = await api.getRooms(pageOffSet);
			this.pageOffSet = pageOffSet;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public createOrEditRoom = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			if (!this.selectedRoom) {
				const newRoom: api.NewRoom = {
					name: this.name,
					nick: this.nick,
					description: this.description,
					url: this.url ? this.url : null,
					images: this.imageService.getUncertainfiedImages(),
					layouts: this.layoutsImageService.getUncertainfiedImages(),
					order: +this.order,
				};
				this.selectedRoom = await api.createRoom(newRoom);
			} else {
				const editedRoom: api.EditRoom = {
					language: languageStore.selectedLanguage,
					name: this.name,
					nick: this.nick,
					description: this.description,
					url: this.url,
					images: this.imageService.getUncertainfiedImages(),
					layouts: this.layoutsImageService.getUncertainfiedImages(),
					order: +this.order,
				};
				this.selectedRoom = await api.editRoom(this.selectedRoom.id, editedRoom);
			}

			routerStore.replace("/dashboard/rooms");
			window.location.reload();
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public deleteRoom = async (roomId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			await api.deleteRoom(roomId);
			this.rooms = this.rooms.filter((room) => room.id !== roomId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public openDeleteRoomDialog = async (roomId: string, roomName: string) => {
		uiStore.showDialog(
			{
				title: strings.rooms.deleteRoomDialog.title,
				message: `${strings.rooms.deleteRoomDialog.message}: ${roomName}`,
			},
			() => this.deleteRoom(roomId),
		);
	};

	@action
	public nextPage = async () => {
		await this.getRooms(this.pageOffSet + 1);
	};

	@action
	public previousPage = async () => {
		if (this.pageOffSet > 0) {
			await this.getRooms(this.pageOffSet - 1);
		}
	};

	@action
	public clear = () => {
		this.imageService.clear();
		this.layoutsImageService.clear();
		this.name = "";
		this.nick = "";
		this.description = "";
		this.url = "";
	};

	@action
	public setEditorFields = (room: api.Room) => {
		const roomInSelectedLanguage = room.languages.filter((roomLanguage) =>
		roomLanguage.language === languageStore.selectedLanguage)[0].translation;

		this.imageService.setImages(roomInSelectedLanguage.images);
		this.layoutsImageService.setImages(roomInSelectedLanguage.layouts);
		this.name = roomInSelectedLanguage.name;
		this.nick = roomInSelectedLanguage.nick;
		this.description = roomInSelectedLanguage.description ? roomInSelectedLanguage.description : "";
		this.url = roomInSelectedLanguage.url ? roomInSelectedLanguage.url : "";
	};

	@action
	public startRoomCreateOrEdit(roomId?: string) {
		if (roomId) {
			this.getRoom(roomId);
			routerStore.push(`/dashboard/rooms/editor/${roomId}`);
		} else {
			this.selectedRoom = null;
			this.clear();
			routerStore.push("/dashboard/rooms/editor/");
		}
	}

}
